/**
 * What the run collides with, and the two flat things it does not: solids and
 * their boot split, decor, and floors.
 *
 * `solidColliders` is the load-bearing one - it is what turns one authored box
 * into the boxes the simulation actually tests, and it is hashed. See
 * `src/config/AGENTS.md`.
 */

import type { Box, MaterialKey } from './levelBox';

/**
 * How `World` should draw a box that is not just a box.
 *
 * This is a *rendering* hint and nothing else: collision only ever sees the
 * boxes `solidColliders()` returns, nothing in `src/sim` reads this field, and
 * the fingerprint serialises those boxes and their names only. A car can
 * therefore be given wheels, or a cable a sag, without retiring a single
 * leaderboard time. `SolidSpec.boot` is the one neighbour of this field that
 * is *not* like that - see it.
 *
 * `'tent'` is the pop-up on the piha: the box is its four walls, and what
 * stands over the top of it - the printed valance and the canopy - is drawn
 * above the collider rather than authored as decor, because a canopy is a
 * pyramid and every box in this file is a box.
 *
 * Two of the values mean "somebody else draws this". `'gate'` is the veräjä,
 * whose leaf has to swing, so `World.buildVeraja` builds it hinged and
 * `buildSolids` leaves it alone - without that there would be two of it, one
 * swinging and one nailed shut, and the shut one is the one the player would
 * see. `'invisible'` is for a box whose whole appearance is decor standing in
 * the same place: the klapiteline is its frame, its canopy and forty billets,
 * and a plain box drawn at the same size would simply cover all of them.
 */
export type SolidShape = 'car' | 'camper' | 'tractor' | 'trailer' | 'drawbar' | 'cable' | 'gate' | 'tent' | 'invisible';

/**
 * The tail end of a car, which is not as tall as the rest of it.
 *
 * A boot lid stands a metre below the roofline, and on a car reversed into a
 * bay it is the low end that is sticking out into the lane. That is the whole
 * of the shortcut in the parkkipaikka: with about half the vauhti meter earned
 * the apex clears `height` and you vault the tail instead of steering round it
 * (see `MOMENTUM.jumpBoost`), and with it empty you do not.
 *
 * Unlike `shape`, this is *not* render-only: it splits the collider in two, so
 * it is hashed and moving it retires the board. `solidColliders()` is the one
 * place that split happens, and both the physics and the drawn car read it
 * from there.
 */
export interface BootSpec {
  /** How much of the car's length is boot lid, measured from `end`. */
  length: number;
  /** How high that stretch stands. Below the body, above a cold jump. */
  height: number;
  /** Which end of the long axis it is: -1 for the box's min side, +1 for max. */
  end: -1 | 1;
}

/**
 * A marque, for the two cars in the ansalanka row that are meant to be
 * recognised across the yard.
 *
 * Render-only, and named rather than inferred from the paint: the bemari used
 * to get its roundel because it happened to be the one car painted `carEv`,
 * which meant repainting a car re-badged it. A car wears the badge it is given.
 */
export type CarBadge = 'bmw' | 'audi';

/** A box that is both drawn and collided with. */
export interface SolidSpec {
  name: string;
  box: Box;
  material: MaterialKey;
  shape?: SolidShape;
  /**
   * Registration, for a `shape: 'car'` or a `shape: 'camper'`. Every vehicle in
   * the level has one; see `plateFor` in src/config/level.ts for where they
   * come from. Render-only, like the shape itself - `simulationFingerprint()`
   * hashes a solid's colliders and nothing else, so lettering a car never costs
   * a leaderboard time.
   */
  plate?: string;
  /** Grille and badge, for a `shape: 'car'`. Render-only, like the plate. */
  badge?: CarBadge;
  /** Set on a car reversed into its bay. Collision, not decoration. */
  boot?: BootSpec;
}

/**
 * What the boot half of a split solid is called.
 *
 * Shared rather than spelled twice: `solidColliders` names the lid and
 * `populateCollisionWorld` has to recognise it again to tag it, and a suffix
 * that agreed in one place and not the other would silently stop a car's lid
 * being a lid. See `MOMENTUM.jumpFactor` for what hangs off that tag.
 */
export const BOOT_SUFFIX = '-pera';

/**
 * Every collider one solid contributes, as `[id, box]`.
 *
 * One for almost everything. Two for a car with a boot, because the collider a
 * player meets at the tail is not the one they meet at the windscreen - and
 * because a shape that is jumpable at one end and not at the other cannot be a
 * single axis-aligned box.
 *
 * The physics, the fingerprint and the F4 overlay all go through here, which
 * is the point: there is one answer to "what is actually solid", and nothing
 * gets to hold a second opinion.
 */
export function solidColliders(spec: SolidSpec): [string, Box][] {
  const boot = spec.boot;
  if (!boot) return [[spec.name, spec.box]];

  const b = spec.box;
  const alongZ = b.maxZ - b.minZ > b.maxX - b.minX;
  const min = alongZ ? b.minZ : b.minX;
  const max = alongZ ? b.maxZ : b.maxX;
  const cut = boot.end > 0 ? max - boot.length : min + boot.length;
  const bodyY = { minY: b.minY, maxY: b.maxY };
  const bootY = { minY: b.minY, maxY: b.minY + boot.height };

  const span = (from: number, to: number, y: { minY: number; maxY: number }): Box =>
    alongZ
      ? { minX: b.minX, maxX: b.maxX, minZ: from, maxZ: to, ...y }
      : { minX: from, maxX: to, minZ: b.minZ, maxZ: b.maxZ, ...y };

  const lid = `${spec.name}${BOOT_SUFFIX}`;
  return boot.end > 0
    ? [
        [spec.name, span(min, cut, bodyY)],
        [lid, span(cut, max, bootY)],
      ]
    : [
        [lid, span(min, cut, bootY)],
        [spec.name, span(cut, max, bodyY)],
      ];
}

/** A box that is only drawn (rugs, monitors, roofs, cabling, ...). */
export interface DecorSpec {
  name: string;
  box: Box;
  material: MaterialKey;
}

/** A flat, optionally rotated quad used for floors and ground patches. */
export interface FloorSpec {
  name: string;
  center: [number, number];
  size: [number, number];
  y: number;
  material: MaterialKey;
  rotationY?: number;
}
